// Global Instructions Rule Applied!

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface ScheduleOfInvestmentsResponse {
  data: Record<string, unknown>[]
  error?: string
}

/**
 * Fetches Schedule of Investments data from the report endpoint.
 * @param fundIds - Optional list of fund UUIDs. If empty, returns all funds.
 * @param datetime - Optional ISO date string (YYYY-MM-DD) or datetime. Uses end of day for date-only.
 * @param pByFund - Maps to p_by_fund on view__soi (default true).
 * @param pByVintage - Maps to p_by_vintage on view__soi (default true).
 * @param pByTranche - Maps to p_by_tranche on view__soi (default false).
 * @param accessToken - User's access token from Redux store
 */
export const fetchScheduleOfInvestments = async (
  fundIds: string[] | null | undefined,
  datetime: string | null | undefined,
  pByFund: boolean,
  pByVintage: boolean,
  pByTranche: boolean,
  accessToken: string | null = null
): Promise<ScheduleOfInvestmentsResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const body: {
      fund_ids?: string[]
      datetime?: string
      p_by_fund: boolean
      p_by_vintage: boolean
      p_by_tranche: boolean
    } = {
      p_by_fund: pByFund,
      p_by_vintage: pByVintage,
      p_by_tranche: pByTranche
    }
    if (fundIds && fundIds.length > 0) {
      body.fund_ids = fundIds
    }
    if (datetime) {
      body.datetime = datetime
    }

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.REPORTING.SCHEDULE_OF_INVESTMENTS, {}, {}),
      {
        method: API_ROUTES.REPORTING.SCHEDULE_OF_INVESTMENTS.method,
        headers,
        body: JSON.stringify(body)
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch schedule of investments: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: Record<string, unknown>[] }

    return {
      data: Array.isArray(parsed.data) ? parsed.data : []
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching schedule of investments:', error)
    }
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}
